import React, { useCallback, useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Grid, Flex, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';

import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { CreateWithRovoLogoSVG } from './assets/CreateWithRovoLogo.tsx';
import { RovoAnnouncementSplashRightPanel } from './RovoAnnouncementSplashRightPanel.tsx';
import { CarouselControl } from './CarouselControl.tsx';

export const i18n = defineMessages({
	modalTitle: {
		id: 'create-with-rovo-announcement-splash.modal.title',
		defaultMessage: 'Introducing Create with Rovo',
		description: 'Title of modal announcing Create with Rovo feature',
	},
	modalSubtitle: {
		id: 'create-with-rovo-announcement-splash.modal.subtitle',
		defaultMessage:
			"Bye-bye, blank page. Rovo can create what you need with a simple description and the power of your team's data.",
		description: 'Subtitle of modal announcing Create with Rovo feature',
	},
	modalMenuItemDescribe: {
		id: 'create-with-rovo-announcement-splash.modal.menu.item.describe',
		defaultMessage: 'Describe what to create, let Rovo start it',
		description: 'Clickable item to describe create with rovo',
	},
	modalMenuItemWhiteboard: {
		id: 'create-with-rovo-announcement-splash.modal.menu.item.whiteboard',
		defaultMessage: 'Put an idea on a page, let Rovo whiteboard it',
		description: 'Clickable item to describe create with rovo',
	},
	modalMenuItemEdit: {
		id: 'create-with-rovo-announcement-splash.modal.menu.item.edit',
		defaultMessage: 'Brain dump thoughts on a page, let Rovo edit it',
		description: 'Clickable item to describe create with rovo',
	},
	modalButtonText: {
		id: 'create-with-rovo-announcement-splash.modal.button.text',
		defaultMessage: 'Try Create with Rovo',
		description: 'Button text to try Create with Rovo feature',
	},
	dismissButtonLabel: {
		id: 'create-with-rovo-announcement-splash.modal.dismiss.button.label',
		defaultMessage: 'Dismiss',
		description:
			'The text is shown a label on the dismiss button for the create with rovo announcement modal.',
	},
});

const styles = cssMap({
	container: {
		position: 'relative',
		width: '100%',
		height: '100%',
		borderRadius: 'inherit',
		clipPath: 'border-box',
	},
	grid: {
		gridTemplateColumns: '1fr 1fr',
		minHeight: '568px',
	},
	closeButton: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
	},
	textContainer: {
		height: '100%',
		paddingTop: token('space.600'),
		paddingBottom: token('space.500'),
		paddingInline: token('space.500'),
	},
	titleContainer: {
		flex: '1 1 auto',
		gap: token('space.250'),
	},
	menuItem: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.100'),
		padding: token('space.150'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.medium'),
		textAlign: 'left',

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	activeMenuItem: {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	ctaButtonContainer: {
		paddingTop: token('space.150'),
	},
	carouselControl: {
		position: 'absolute',
		bottom: token('space.200'),
		right: token('space.200'),
	},
});

const ANALYTICS_SOURCE = 'createWithRovoAnnouncementSplashModal';

// Label for analytic attribute
const menuItemLabels = [
	i18n.modalMenuItemDescribe.defaultMessage,
	i18n.modalMenuItemWhiteboard.defaultMessage,
	i18n.modalMenuItemEdit.defaultMessage,
];

type RovoAnnouncementSplashModalProps = {
	onModalShown?: (dismissSource: string) => void;
};

export const RovoAnnouncementSplashModalComponent = ({
	onModalShown,
}: RovoAnnouncementSplashModalProps) => {
	const { formatMessage } = useIntl();
	const { openModal } = useSmartCreationModalActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [selectedGif, setSelectedGif] = useState<number>(0);

	const [isAutoRotationEnabled, setIsAutoRotationEnabled] = useState<boolean>(true);

	useEffect(() => {
		const intervalId = setInterval(() => {
			if (isAutoRotationEnabled) {
				setSelectedGif((prevSelectedGif) => (prevSelectedGif + 1) % 3);
			}
		}, 5000);

		return () => clearInterval(intervalId);
	}, [isAutoRotationEnabled]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'createWithRovoAnnouncementSplashModal',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleCTAClick = useCallback(() => {
		openModal('createWithRovoAnnouncementSplashModal', '');
		onModalShown?.('tryRovo');
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryCreateWithRovo',
				source: ANALYTICS_SOURCE,
			},
		}).fire();
	}, [createAnalyticsEvent, onModalShown, openModal]);

	const handleMenuItemClick = useCallback(
		(index: number) => {
			setIsAutoRotationEnabled(false);

			setSelectedGif(index);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createWithRovoAnnouncementSplashModalMenuItem',
					attributes: {
						menuItem: menuItemLabels[index],
					},
					source: ANALYTICS_SOURCE,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleDismiss = useCallback(() => {
		onModalShown?.('dismissButton');
	}, [onModalShown]);

	const handleCarouselControlClick = useCallback(() => {
		setIsAutoRotationEnabled((prev) => !prev);
	}, []);

	return (
		<Flex xcss={styles.container}>
			<Grid xcss={styles.grid}>
				<Stack grow="fill" space="space.0" xcss={styles.textContainer}>
					<Stack alignInline="start" space="space.0">
						<CreateWithRovoLogoSVG />
					</Stack>
					<Stack xcss={styles.titleContainer}>
						<Stack alignInline="start" space="space.150">
							<Stack alignInline="start" space="space.100">
								<Inline grow="fill" alignInline="center">
									<Heading size="large">{formatMessage(i18n.modalTitle)}</Heading>
								</Inline>
							</Stack>
							<Text>{formatMessage(i18n.modalSubtitle)}</Text>
						</Stack>
						<Stack space="space.100" alignInline="start">
							<Pressable
								xcss={cx(styles.menuItem, selectedGif === 0 && styles.activeMenuItem)}
								onClick={() => handleMenuItemClick(0)}
							>
								<Flex direction="row" alignItems="center" gap="space.100">
									<RovoIcon label="" />
									<Text>{formatMessage(i18n.modalMenuItemDescribe)}</Text>
								</Flex>
							</Pressable>

							<Pressable
								xcss={cx(styles.menuItem, selectedGif === 1 && styles.activeMenuItem)}
								onClick={() => handleMenuItemClick(1)}
							>
								<WhiteboardIcon label="" />
								<Text>{formatMessage(i18n.modalMenuItemWhiteboard)}</Text>
							</Pressable>

							<Pressable
								xcss={cx(styles.menuItem, selectedGif === 2 && styles.activeMenuItem)}
								onClick={() => handleMenuItemClick(2)}
							>
								<PageLiveDocIcon label="" />
								<Text>{formatMessage(i18n.modalMenuItemEdit)}</Text>
							</Pressable>
						</Stack>
					</Stack>
					<Stack xcss={styles.ctaButtonContainer} alignInline="end">
						<Button appearance="primary" onClick={() => handleCTAClick()} autoFocus>
							{formatMessage(i18n.modalButtonText)}
						</Button>
					</Stack>
				</Stack>
				<RovoAnnouncementSplashRightPanel frame={selectedGif} />
				<Flex xcss={styles.closeButton}>
					<IconButton
						appearance="subtle"
						onClick={() => handleDismiss()}
						label={formatMessage(i18n.dismissButtonLabel)}
						icon={(iconProps) => <CrossIcon {...iconProps} color={token('color.icon')} />}
					/>
				</Flex>
				<Flex xcss={styles.carouselControl}>
					<CarouselControl
						isPlaying={isAutoRotationEnabled}
						onClick={() => handleCarouselControlClick()}
					/>
				</Flex>
			</Grid>
		</Flex>
	);
};
